import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Context que expõe o estado da extração de dados da consulta atual pra
 * componentes fora do PatientSessionPanel (especificamente a sidebar, que
 * tem um atalho "Extrair Dados" espelhando o "Gravar áudio").
 *
 * O PatientSessionPanel é o dono da lógica real (`submitExtraction`,
 * cálculo de `hasAnyInput`, busca de drafts, etc.). Ele registra um
 * trigger e reporta o estado via `setState`. A sidebar só consome.
 *
 * Quando nenhum PatientSessionPanel está montado (fora de `/pacientes/:id`),
 * `hasAnyInput` continua false e `trigger` é no-op — o botão da sidebar
 * fica desabilitado automaticamente.
 */
interface ExtractionContextValue {
  hasAnyInput: boolean;
  isExtracting: boolean;
  /** Chamado pela sidebar pra disparar a extração no panel ativo. */
  trigger: () => void;
  /** Chamado pela sidebar pra INTERROMPER a extração em andamento. */
  cancel: () => void;
  /** Usado pelo PatientSessionPanel pra publicar mudanças de estado. */
  setState: (state: { hasAnyInput: boolean; isExtracting: boolean }) => void;
  /** Usado pelo PatientSessionPanel pra instalar/remover o handler real.
   *  Retorna função de unsubscribe. */
  registerTrigger: (fn: () => void) => () => void;
  /** Mesmo contrato do registerTrigger, para o handler de interromper. */
  registerCancel: (fn: () => void) => () => void;
}

const ExtractionContext = createContext<ExtractionContextValue | null>(null);

export const ExtractionProvider = ({ children }: { children: ReactNode }) => {
  const [hasAnyInput, setHasAnyInput] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const triggerRef = useRef<(() => void) | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const setState = useCallback(
    (s: { hasAnyInput: boolean; isExtracting: boolean }) => {
      setHasAnyInput(s.hasAnyInput);
      setIsExtracting(s.isExtracting);
    },
    [],
  );

  const registerTrigger = useCallback((fn: () => void) => {
    triggerRef.current = fn;
    return () => {
      if (triggerRef.current === fn) triggerRef.current = null;
    };
  }, []);

  const registerCancel = useCallback((fn: () => void) => {
    cancelRef.current = fn;
    return () => {
      if (cancelRef.current === fn) cancelRef.current = null;
    };
  }, []);

  const trigger = useCallback(() => {
    triggerRef.current?.();
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current?.();
  }, []);

  // Memoiza o value: sem isto, todo `setState` recria o objeto, mudando a
  // identidade do context a cada render — o que faz os efeitos que dependem
  // dele (registerTrigger/report de estado nos painéis) re-rodarem à toa.
  const value = useMemo(
    () => ({ hasAnyInput, isExtracting, trigger, cancel, setState, registerTrigger, registerCancel }),
    [hasAnyInput, isExtracting, trigger, cancel, setState, registerTrigger, registerCancel],
  );

  return (
    <ExtractionContext.Provider value={value}>
      {children}
    </ExtractionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalExtraction = (): ExtractionContextValue | null => {
  return useContext(ExtractionContext);
};
